import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map, take, timeout } from 'rxjs/operators';
import {
  ClpConnectionState,
  IClpError,
  IClpLpPriceEvent,
  IClpPriceSubscriptionReq,
  IClpPriceSubscriptionRes,
  IClpPriceUnsubscriptionReq,
  IClpPriceUnsubscriptionRes,
  IClpServerMessage,
  IClpSymbolPriceEvent,
} from '../clpApi.interface';
import { decodeServerMessage, encodeClientMessage } from '../_helpers/codec';
import { resolveClpWsUrl } from '../_helpers/resolveClpWsUrl';
import { buildClpAuthCookie, clearClpAuthCookie } from '../_helpers/clpAuthCookie';

const RECONNECT_DELAY_MS = 5000;
const REQUEST_TIMEOUT_MS = 10_000;

/** The slice of `AuthService` the transport needs; structural so api-clp stays auth-package-free. */
interface IClpAuthProvider {
  getApiToken(): string | undefined;
  /** Refreshes the token if it is near expiry (resolves immediately otherwise). Optional for tests. */
  updateToken?(): Promise<boolean>;
}

/**
 * WebSocket transport for the CLP price-streaming service (LB-A-1156).
 *
 * Wraps the native browser `WebSocket` (binary protobuf frames) — intentionally NOT `BaseService`
 * (axios) nor `rxjs/webSocket` (its JSON serializer fights binary framing). Exposes RxJS streams for
 * price events and request/response correlation by `request_id`. Auto-reconnects every 5 s on drop.
 *
 * Auth: CLP reads the JWT from a `token` cookie on the upgrade (LB-3633) — the browser cannot set
 * headers on a WebSocket. Each connect attempt refreshes a near-expiry token, then (re)writes the
 * cookie, so every reconnect carries a fresh token (CLP caps the session at JWT expiry, dropping it
 * exactly when the token expires — the reconnect must not reuse the just-expired one).
 */
export class ClpApiService {
  // Overridable for tests (fast reconnect against a local WS server); prod uses the default.
  constructor(private readonly options: { reconnectDelayMs?: number } = {}) {}

  private auth: IClpAuthProvider | null = null;

  public setAuth(auth: IClpAuthProvider): void {
    this.auth = auth;
  }

  private socket: WebSocket | null = null;
  private url: string | null = null;
  private shouldReconnect = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private outbox: Uint8Array[] = [];

  private readonly serverMessageSubject = new Subject<IClpServerMessage>();
  private readonly connectionStateSubject = new BehaviorSubject<ClpConnectionState>(ClpConnectionState.Idle);

  public readonly symbolPriceEvent$ = new Subject<IClpSymbolPriceEvent>();
  public readonly lpPriceEvent$ = new Subject<IClpLpPriceEvent>();
  public readonly error$ = new Subject<IClpError>();
  public readonly connectionState$ = this.connectionStateSubject.asObservable();

  /** Opens the WS session and resolves once the socket reaches the `Open` state. */
  public open$({ clientId }: { clientId?: string } = {}): Observable<void> {
    this.shouldReconnect = true;
    this.url = resolveClpWsUrl(clientId);
    this.connectionStateSubject.next(ClpConnectionState.Connecting);
    this.connect();

    return this.connectionState$.pipe(
      filter((state) => state === ClpConnectionState.Open),
      take(1),
      map(() => undefined),
      timeout(REQUEST_TIMEOUT_MS),
    );
  }

  /** Sends a subscription request and completes with the matching `PriceSubscriptionRes`. */
  public subscribe$(request: IClpPriceSubscriptionReq): Observable<IClpPriceSubscriptionRes> {
    this.send(encodeClientMessage({ priceSubscription: request }));

    return this.serverMessageSubject.pipe(
      map((message) => message.priceSubscription),
      filter((res): res is IClpPriceSubscriptionRes => !!res && res.requestId === request.requestId),
      take(1),
      timeout(REQUEST_TIMEOUT_MS),
    );
  }

  /** Sends an unsubscription request and completes with the matching `PriceUnsubscriptionRes`. */
  public unsubscribe$(request: IClpPriceUnsubscriptionReq): Observable<IClpPriceUnsubscriptionRes> {
    this.send(encodeClientMessage({ priceUnsubscription: request }));

    return this.serverMessageSubject.pipe(
      map((message) => message.priceUnsubscription),
      filter((res): res is IClpPriceUnsubscriptionRes => !!res && res.requestId === request.requestId),
      take(1),
      timeout(REQUEST_TIMEOUT_MS),
    );
  }

  /** Closes the session and stops reconnecting. Also drops the auth cookie so the JWT is not left
   * in the cookie jar (scoped to `/clp`, it would otherwise ride every later request, e.g. a next
   * user on a shared browser). */
  public close(): void {
    this.shouldReconnect = false;
    this.clearReconnectTimer();
    this.outbox = [];
    this.discardSocket();
    this.clearAuthCookie();
    this.connectionStateSubject.next(ClpConnectionState.Closed);
  }

  private connect(): void {
    if (!this.url) {
      return;
    }
    this.clearReconnectTimer();
    // CLP caps the session at JWT expiry, so a routine drop lands exactly when the token expires;
    // refresh a near-expiry token before (re)connecting so the reconnect carries a fresh one, not
    // the just-expired token. `updateToken` resolves immediately when the token is still valid; a
    // refresh failure still proceeds (CLP will reject a truly-expired token, triggering a retry).
    const refresh = this.auth?.updateToken?.();
    if (refresh) {
      refresh.then(() => this.openSocket(), () => this.openSocket());
    } else {
      this.openSocket();
    }
  }

  private openSocket(): void {
    // A `close()` during the async token refresh must not still open a socket.
    if (!this.shouldReconnect || !this.url) {
      return;
    }
    // Two connects can be in flight (a clientId change dispatches disconnect+connect, and the async
    // token refresh widens the window): retire any prior socket before opening a new one so we never
    // leave an orphan whose events would corrupt the live session.
    this.discardSocket();
    this.applyAuthCookie();

    const socket = new WebSocket(this.url);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    // Every handler ignores events from a socket that is no longer the current one.
    socket.onopen = () => {
      if (socket !== this.socket) {
        return;
      }
      this.connectionStateSubject.next(ClpConnectionState.Open);
      this.flushOutbox(socket);
    };
    socket.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      if (socket === this.socket) {
        this.handleMessage(event.data);
      }
    };
    socket.onclose = () => {
      if (socket !== this.socket) {
        return;
      }
      this.socket = null;
      if (this.shouldReconnect) {
        this.connectionStateSubject.next(ClpConnectionState.Reconnecting);
        this.scheduleReconnect();
      } else {
        this.connectionStateSubject.next(ClpConnectionState.Closed);
      }
    };
    socket.onerror = () => {
      if (socket === this.socket) {
        socket.close();
      }
    };
  }

  /** Detaches handlers from the current socket and closes it, so it becomes inert (its late events
   * are ignored and its `onclose` no longer drives the reconnect logic). */
  private discardSocket(): void {
    const socket = this.socket;
    if (!socket) {
      return;
    }
    this.socket = null;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    socket.close();
  }

  /** Refreshes the `token` cookie from the auth provider; no-op outside a browser (tests). */
  private applyAuthCookie(): void {
    const token = this.auth?.getApiToken();
    if (!token || !this.url || typeof document === 'undefined') {
      return;
    }
    document.cookie = buildClpAuthCookie(token, this.url.startsWith('wss:'));
  }

  /** Expires the `token` cookie; no-op outside a browser (tests). */
  private clearAuthCookie(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.cookie = clearClpAuthCookie();
  }

  private handleMessage(data: ArrayBuffer): void {
    let message: IClpServerMessage;
    try {
      message = decodeServerMessage(new Uint8Array(data));
    } catch {
      return;
    }
    if (message.symbolPrice) {
      this.symbolPriceEvent$.next(message.symbolPrice);
    }
    if (message.lpPrice) {
      this.lpPriceEvent$.next(message.lpPrice);
    }
    if (message.error) {
      this.error$.next(message.error);
    }
    this.serverMessageSubject.next(message);
  }

  private send(bytes: Uint8Array): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(bytes);
    } else {
      this.outbox.push(bytes);
    }
  }

  private flushOutbox(socket: WebSocket): void {
    const pending = this.outbox;
    this.outbox = [];
    pending.forEach((bytes) => {
      try {
        socket.send(bytes);
      } catch {
        // Not actually writable — re-queue so the frame is not lost.
        this.outbox.push(bytes);
      }
    });
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => this.connect(), this.options.reconnectDelayMs ?? RECONNECT_DELAY_MS);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
